<template>
  <div v-if="data.object" class="event-form text-left">
    
    <Message v-if="data.formStatus === 'error'" severity="error">
      Please ensure that you have filled out the form entirely and correctly
    </Message>
    <Message v-if="!canBuyPrimary && !canBuyGuest" severity="warn">
      You have added the maximum allowed tickets
    </Message>
    <form v-else @submit.prevent="isValid" ref="form" class="gap-sm">
      <fieldset class="flex-column gap-sm">
        <div v-if="canBuyGuest" class="flex-row gap-sm">
          <label
            class="checkbox-row flex-row gap-sm"
            :class="{ disabled: !canBuyPrimary || !canBuyGuest }"
          >
            <!-- :model-value="!data.properties.guest"
            @update:model-value="data.properties.guest = $event" -->
            <Checkbox
              @change="emits('change')"
              v-model="primary"
              binary
              type="checkbox"
              :disabled="!canBuyPrimary || !canBuyGuest"
            />
            Primary Ticket
          </label>
          <label
            class="checkbox-row flex-row gap-sm"
            :class="{ disabled: !canBuyPrimary || !canBuyGuest }"
          >
            <Checkbox
              @change="emits('change')"
              v-model="data.properties.guest"
              binary
              type="checkbox"
              :disabled="!canBuyPrimary || !canBuyGuest"
            />
            Guest Ticket
          </label>
        </div>
      </fieldset>
      <fieldset v-for="grouper of groups" class="flex-column gap-3xs">
        <!-- <legend v-if="grouper.label">
          {{ grouper.label }}
        </legend> -->
        <template v-for="field of grouper.fields">
          <div v-if="field.field_type === 'checkbox'">
            <label class="checkbox-row flex-row gap-sm overflow">
              <Checkbox
                binary
                v-model="data.properties[field.field_name]"
                @change="emits('change')"
                :name="field.field_name"
                :required="field.is_required"
                :autocomplete="field.autocomplete || 'off'"
              />
              <span>{{ field.label || field.field_name }}</span> <br />
            </label>
            <div v-if="field.help_html" v-html="field.help_html" class="help" />
          </div>
          <div v-else class="flex-column gap-3xs">
            <label>{{ field.label || field.field_name }}</label>
            <div>
              <InputNumber
                v-if="['number'].includes(field.field_type)"
                v-model="data.properties[field.field_name]"
                @change="emits('change')"
                :type="field.field_type"
                :name="field.field_name"
                :required="field.is_required"
                :autocomplete="field.autocomplete || 'off'"
                maxlength="255"
              />
              <InputText
                v-else-if="['email', 'text'].includes(field.field_type)"
                v-model="data.properties[field.field_name]"
                @change="emits('change')"
                :type="field.field_type"
                :name="field.field_name"
                :required="field.is_required"
                :autocomplete="field.autocomplete || 'off'"
                maxlength="255"
              />
              <Textarea
                v-else-if="field.field_type === 'textarea'"
                v-model="data.properties[field.field_name]"
                @change="emits('change')"
                :name="field.field_name"
                :required="field.is_required"
                :autocomplete="field.autocomplete || 'off'"
                maxlength="1000"
              />
              <Dropdown
                v-else-if="field.field_type === 'select'"
                v-model="data.properties[field.field_name]"
                @change="emits('change')"
                :name="field.field_name"
                :required="field.is_required"
                :autocomplete="field.autocomplete || 'off'"
                :options="field.choices"
              />
              <div
                v-if="field.help_html"
                v-html="field.help_html"
                class="help"
              />
            </div>
          </div>
        </template>
      </fieldset>
      <div v-if="canBuyGuest || canBuyPrimary">
        <slot>
          <div class="form-row">
            <label></label>
            <div>
              <Button class="btn btn-default">Save</Button>
            </div>
          </div>
        </slot>
      </div>
    </form>
  </div>
  <div v-else-if="data.status === 'loading'" class="p-md">
    <i>Loading...</i>
  </div>
  <div v-else>
    <i>Product form failed to load</i>
    {{ data.status }}
    <Button>Try Again</Button>
  </div>
  <div v-if="sourceProblem" class="note m-sm-b">
    NOTE: Event tickets must be purchased separately from all other products.
    Please empty your shopping bag or move the items you’ve selected to your
    wishlist before completing your event registration.
  </div>
</template>

<script setup lang="ts">
import { useCartStore } from "@/stores/cart";
import type { Category } from "@/stores/defs/shop_defs";
import { useShopStore } from "@/stores/shop";
import type { Product } from "@paparazzi/types/paparazzi.api.serializers";
import type {
  EventForm,
  EventFormEntry,
  EventFormField,
} from "@paparazzi/types/paparazzi.eventforms.serializers";
import api from "@virgodev/bazaar/functions/api";
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import Dropdown from "primevue/dropdown";
import InputNumber from "primevue/inputnumber";
import InputText from "primevue/inputtext";
import Message from "primevue/message";
import Textarea from "primevue/textarea";
import { computed, nextTick, onMounted, reactive, ref, watch } from "vue";

const emits = defineEmits(["update:values", "change", "error"]);
const props = defineProps<{
  product: Product;
  form: string | number;
  values?: Record<string, any>;
}>();

const cart = useCartStore();
const shop = useShopStore();

const form = ref();
const data = reactive({
  status: "",
  formStatus: "",
  properties: props.values || {},
  existing: undefined,
  object: undefined,
} as {
  status: string;
  formStatus: string;
  properties: Record<string, any>;
  existing?: EventFormEntry[];
  object?: EventForm;
});

const primaryCategory = computed(() => {
  let category = props.product.category;
  return shop.allowedCategories.find((c: Category) => c.id === category);
});
const itemSource = computed(() => {
  if (primaryCategory.value) {
    return primaryCategory.value.inventory_source || "default";
  }
  return "default";
});
const cartSource = computed(() => cart.inventorySource);
const sourceProblem = computed(() => {
  return itemSource.value != cartSource.value && cart.items.length > 0;
});

const groups = computed(() => {
  const retval = [];
  if (data.object?.fields) {
    let grouper = { label: "", fields: [] as EventFormField[] };
    for (const field of data.object.fields.filter(
      (f: EventFormField) => !f.is_internal,
    )) {
      if (field.field_group !== grouper.label) {
        if (grouper.fields.length > 0) {
          retval.push(grouper);
        }
        grouper = { label: field.field_group || "", fields: [] };
      }
      grouper.fields.push(field);
    }
    if (grouper.fields.length > 0) {
      retval.push(grouper);
    }
  }
  return retval;
});

const maxGuests = computed(() => {
  return data.object?.max_guests || 1000;
});

const primary = computed({
  get: () => !data.properties.guest,
  set: (v) => (data.properties.guest = !v),
});

const hasPrimaryTicket = computed(() => {
  if (data.existing?.some((s) => !s.guest)) {
    return true;
  }

  if (data.object) {
    for (const item of cart.items) {
      const forms = item.extras?.eventforms;
      if (
        forms &&
        forms[data.object.form_name] &&
        forms[data.object.form_name].guest === false
      ) {
        return true;
      }
    }
  }
  return false;
});

const guestTickets = computed(() => {
  const retval = data.existing ? data.existing.filter((e) => e.guest) : [];
  if (data.object) {
    for (const item of cart.items) {
      const forms = item.extras?.eventforms;
      if (forms && forms[data.object.form_name]?.guest) {
        retval.push(forms[data.object.form_name]);
      }
    }
  }
  return retval;
});
const canBuyPrimary = computed(() => {
  return !hasPrimaryTicket.value;
});
const canBuyGuest = computed(() => {
  if (data.object && data.existing) {
    return maxGuests.value > guestTickets.value.length;
  }
  return null;
});

watch(
  () => props.values,
  () => {
    if (props.values) {
      data.properties = props.values;
    }
  },
  { deep: true },
);
watch([canBuyGuest, canBuyPrimary], checkIfPossible);

onMounted(async () => {
  await loadForm();
  const response = await api({
    url: `event_form_entries/`,
    params: {
      form: props.form,
    },
  });
  if (response.ok) {
    data.existing = response.body.results as EventFormEntry[];
    await nextTick();
    if (!canBuyPrimary.value && canBuyGuest.value) {
      emits("update:values", { guest: true });
    } else {
      emits("update:values", { guest: false });
    }
  }
});

function checkIfPossible() {
  if (!canBuyPrimary.value && !canBuyGuest.value) {
    emits(
      "error",
      `Maximum tickets reached (${guestTickets.value.length}/${maxGuests.value})`,
    );
  } else {
    emits("error", undefined);
  }
}

async function loadForm() {
  data.status = "loading";
  try {
    const response = await api({ url: `event_forms/${props.form}/` });
    if (response.ok) {
      if (response.body.detail === "Not found.") {
        throw new Error(response.body.detail);
      }
      data.object = response.body as EventForm;
      if (Object.keys(data.properties).length === 0) {
        clearForm();
      }
    }
    data.status = "";
  } catch (ex) {
    console.error(ex);
    data.status = `${ex}`;
    data.object = undefined;
  }
}

function clearForm() {
  const retval: Record<string, any> = { guest: !canBuyPrimary.value };
  if (data.object?.fields) {
    data.object.fields.forEach((f: EventFormField) => {
      if (f.field_type === "checkbox") {
        retval[f.field_name] = !!f.default_value;
      } else {
        retval[f.field_name] = f.default_value || null;
      }
    });
  }
  emits("update:values", retval);
  data.properties = retval;
}

function isValid() {
  data.formStatus = "";
  if (data.object) {
    if (form.value.checkValidity()) {
      const retval: Record<string, any> = {};
      for (const prop in data.properties) {
        retval[prop] = data.properties[prop];
      }
      emits("update:values", retval);
      return retval;
    } else {
      data.formStatus = "error";
    }
  }
  form.value.reportValidity();
  return null;
}

defineExpose({
  data: data,
  isValid,
  clearForm,
});
</script>

<style scoped>
fieldset {
  padding-bottom: 5px;
}

.eventforms {
  width: 100%;
}
.event-form {
  margin: 0 -15px 0 -15px;
  width: 100%;
  min-width: 460px;
  max-width: 500px;
}
.event-form p {
  white-space: wrap;
}

.event-form b.title {
  font-size: 300;
}
.event-form legend {
  width: 100%;
  padding: var(--2XS) 0;
  border-bottom: 1px solid var(--30);
}
.event-form input,
.event-form .p-dropdown {
  width: 100%;
}
.event-form label {
  text-transform: uppercase;
  font-size: 14px;
  font-weight: 300;
}
.event-form :deep(.help) {
  margin-top: 10px !important;
}

.event-form :deep(h3) {
  margin: 0;
  font-weight: 400;
  font-size: 16px;
}

.disabled :deep(input[disabled]) {
  border: 2px dashed red;
  opacity: 0.015 !important;
}

.checkbox-row.overflow {
  text-wrap: pretty;
}
.note {
  width: auto;
  text-wrap: pretty;
  text-align: left;
  font-size: 14px;
  color: var(--Paparazzi-Pink);
  line-height: normal;
}

@media (max-width: 900px) {
  .event-form {
    max-width: 460px;
    min-width: 330px;
  }
}

@media (max-width: 800px) and (min-width: 701px) {
  .event-form {
    min-width: 200px;
    max-width: 300px;
  }
}

@media (max-width: 700px) {
  .event-form {
    width: 100vw;
    min-width: 100%;
    max-width: 100%;
  }
}
</style>
